
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as moment from 'moment';

// A custom validator class for validating South African ID numbers
export class SaIdValidator {
    // The main validation function to check if an ID is valid
    static isValidSaId(): ValidatorFn {
        // Returns a validator function that takes an AbstractControl (a form control) and returns validation errors or null
        return (control: AbstractControl): ValidationErrors | null => {
            const idNumber = control.value; // Get the ID number input from the form control

            // Remove any non-digit characters (like spaces, hyphens) to ensure only digits are processed
            const cleanedId = idNumber ? idNumber.replace(/\D/g, '') : '';

            // If the input is empty, return null (no validation error), assuming it's an optional field
            if (cleanedId.length == 0) {
                return null;
            }

            // Validate that the ID number is exactly 13 digits long
            if (cleanedId.length !== 13) {
                return { invalidSaId: 'ID number must be 13 digits long.' }; // Return an error object if length is incorrect
            }

            // Ensure the ID number contains only numeric characters
            if (!/^\d{13}$/.test(cleanedId)) {
                return { invalidSaId: 'ID number must contain only digits.' }; // Return an error object if any character is not a digit
            }

            // Extract and validate the date of birth from the ID number
            const dateOfBirth = SaIdValidator.extractDateOfBirth(cleanedId);
            if (!dateOfBirth) {
                return { invalidSaId: 'Invalid date of birth in ID number.' }; // Return an error if the date of birth is invalid
            }

            // Perform a Luhn algorithm check to verify the checksum of the ID number
            if (!SaIdValidator.luhnCheck(cleanedId)) {
                return { invalidSaId: 'Invalid checksum for ID number.' }; // Return an error if the checksum validation fails
            }

            // If all validation checks are passed, return null (indicating no validation errors)
            return null;
        };
    }

    // Extracts the date of birth from the first six digits of the ID number (YYMMDD format)
    static extractDateOfBirth(idNumber: string): moment.Moment | null {
        const yearPart = idNumber.substr(0, 2); // Get the first 2 digits as the year
        const monthPart = idNumber.substr(2, 2); // Get the next 2 digits as the month
        const dayPart = idNumber.substr(4, 2); // Get the last 2 digits as the day

        let year = parseInt(yearPart, 10); // Convert the year part to an integer
        const month = parseInt(monthPart, 10); // Convert the month part to an integer
        const day = parseInt(dayPart, 10); // Convert the day part to an integer

        // Determine the century by checking the current year
        const currentYear = new Date().getFullYear() % 100; // Get the last two digits of the current year (e.g., 24 for 2024)
        const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100; // Get the current century (e.g., 2000 or 1900)

        // If the ID's year part is greater than the current year's last two digits, assume it's from the previous century
        if (year > currentYear) {
            year += currentCentury - 100; // Adjust to the previous century
        } else {
            year += currentCentury; // Assume it's in the current century
        }

        // Validate if the extracted year, month, and day form a valid date
        if (!SaIdValidator.isValidDate(year, month, day)) {
            return null; // Return null if the date is invalid
        }

        // Return a Moment object representing the date of birth, adjusting for UTC and setting it to the start of the day
        return moment.utc(`${year}-${month}-${day}`, 'YYYY-MM-DD').startOf('day');
    }

    // Helper function to check if a given year, month, and day form a valid date
    static isValidDate(year: number, month: number, day: number): boolean {
        if (month < 1 || month > 12) {
            return false; // Invalid month
        }

        const daysInMonth = new Date(year, month, 0).getDate(); // Get the number of days in the given month and year
        if (day < 1 || day > daysInMonth) {
            return false; // Invalid day
        }

        return true; // The date is valid
    }

    // Perform a checksum validation using the Luhn algorithm to ensure the ID number is valid
    static luhnCheck(idNumber: string): boolean {
        let sum = 0; // Initialize the checksum accumulator
        const length = idNumber.length; // Get the length of the ID number
        const parity = length % 2; // Determine the parity to check every second digit

        // Loop through each digit of the ID number except the last one (which is the check digit)
        for (let i = 0; i < length - 1; i++) {
            let digit = parseInt(idNumber.charAt(i), 10); // Get the digit at the current position

            // If the current position matches the parity, double the digit
            if (i % 2 === parity) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9; // If the doubled digit is greater than 9, subtract 9 to reduce it
                }
            }

            sum += digit; // Add the digit (or adjusted digit) to the checksum
        }

        // Get the last digit of the ID number, which is the check digit
        const lastDigit = parseInt(idNumber.charAt(length - 1), 10);

        // Return true if the checksum (including the check digit) is divisible by 10, indicating a valid ID number
        return (sum + lastDigit) % 10 === 0;
    }
}
